import { Bookmark, Paragraph, Table, TextRun } from 'docx';
import { EsrsReportMetric, SimplifiedReportNarrativeMetric } from '../../Report.types';
import { TextStyle } from '../reportThemes';
import { QuestionType_Enum_ } from 'models';
import { createMetricTable } from './quantitative-metrics';
import { addEmptySpace } from '../report-helper-functions';

export const createNarrativeAnswer = (
  metric: SimplifiedReportNarrativeMetric,
  hasAnswer: boolean
) => {
  const textAnswer = metric.textAnswer ?? '';
  const answerLines = (hasAnswer ? textAnswer.split('\n') : ['No answer provided.']) ?? [];

  const answerParagraphs = answerLines.map(
    (line) =>
      new Paragraph({
        text: line,
        style: hasAnswer ? TextStyle.body : TextStyle.bodyItalic,
        spacing: {
          afterAutoSpacing: true,
        },
      })
  );

  return answerParagraphs;
};

export const createNarrativeMetric = (
  metric: SimplifiedReportNarrativeMetric,
  isTopLevel: boolean,
  allMetrics: EsrsReportMetric[],
  companyCurrency: string,
  uniqueRowRefs: string[]
) => {
  const { shortTitle, title, reference } = metric.materialMetric.metric;

  const titleParagraph = new Paragraph({
    children: [
      new Bookmark({
        id: reference,
        children: [
          new TextRun({
            text: shortTitle ?? title,
          }),
        ],
      }),
    ],
    style: TextStyle.h4,
    spacing: {
      after: 100,
    },
  });

  const hasAnswer = !!metric?.textAnswer && metric?.textAnswer !== '';
  const isBooleanOrEnum =
    metric.materialMetric.metric.metricType === QuestionType_Enum_.YesNo_ ||
    metric.materialMetric.metric.metricType === QuestionType_Enum_.MultipleChoice_ ||
    metric.materialMetric.metric.metricType === QuestionType_Enum_.SingleChoice_;
  const isBoolean = metric.materialMetric.metric.metricType === QuestionType_Enum_.YesNo_;
  const isStandalone =
    !metric.children.length && !metric.materialMetric.metric.parentMetrics.length;

  const hideTitle = (isBoolean || isStandalone) && hasAnswer;

  // Split answers with multiple lines into paragraphs
  const answerParagraphs = createNarrativeAnswer(metric, hasAnswer);

  const textBefore = metric.isBeforeVisible
    ? [
        new Paragraph({
          text: metric.textBefore,
          style: TextStyle.body,
          spacing: {
            after: 100,
          },
        }),
      ]
    : [];

  const textAfter = metric.isAfterVisible
    ? [
        new Paragraph({
          text: metric.textAfter,
          style: TextStyle.body,
          spacing: {
            before: 100,
          },
        }),
      ]
    : [];

  // For children that are numeric
  const isNumericChild = metric.materialMetric.metric.metricType === QuestionType_Enum_.Decimal_;

  if (isNumericChild) {
    const numericChild = (allMetrics ?? []).find(
      (m) => m.metric.metricRef === metric.materialMetric.metricRef
    );

    if (!numericChild) {
      return [];
    }

    const numericChildSection = [
      titleParagraph,
      createMetricTable(numericChild, companyCurrency, uniqueRowRefs),
    ].flat();

    return [...numericChildSection, addEmptySpace(100)];
  }

  if (metric.children.length > 0 && !metric.isLocked) {
    const nestedChildren: (Paragraph | Table)[] = (
      metric.children.flatMap((child) => {
        const narrativeMetricSection = createNarrativeMetric(
          child,
          false,
          allMetrics,
          companyCurrency,
          uniqueRowRefs
        );

        return [narrativeMetricSection, addEmptySpace(100)];
      }) ?? []
    ).flat();

    const booleanOrEnumParentParagraph = [answerParagraphs, addEmptySpace(50)].flat();

    return [
      hideTitle ? [] : titleParagraph,
      ...textBefore,
      // Case when the metric is a boolean or enum parent, we show its answer, otherwise we show only title
      isBooleanOrEnum ? booleanOrEnumParentParagraph : [],
      nestedChildren,
      ...textAfter,
    ].flat();
  }

  return isTopLevel || !hasAnswer
    ? hideTitle
      ? [...answerParagraphs]
      : [titleParagraph, ...answerParagraphs]
    : [...answerParagraphs];
};
